// useLiveConnection — "is this board still telling the truth?"
//
// ⚠️ THE SAFETY PROPERTY OF THE CONTROL PANEL.
//
// Offline persistence is enabled (firebase/config.js). When a wall display
// loses the network, the Firestore client keeps serving the ENTIRE board out of
// IndexedDB: every room, every case, every name, rendered exactly as normal.
// Nothing about a stale board looks stale. Staffing that is silently wrong is
// worse than staffing that is missing, because people act on it.
//
// Worse, `onSnapshot` without `includeMetadataChanges` does NOT fire on a
// metadata-only change — so the moment `fromCache` flips to true, no callback
// is delivered at all. A panel waiting to be told something is wrong waits
// forever.
//
// TWO INDEPENDENT SOURCES, and it needs both:
//
//   1. THE CANARY — `listenToFacilityMeta` subscribes to the facility doc the
//      panel already reads, but WITH metadata changes, so cache transitions
//      become observable. Metadata-only deliveries are not billed as reads.
//
//   2. THE HEARTBEAT — a wall clock ticking independently of any callback. If
//      the canary never fires, for any reason whatsoever, the age still climbs.
//      A staleness detector that can only be triggered by an event cannot
//      detect the failure where events stop arriving.
//
// Tick interval: 5 s, NOT the panel's shared 30 s NOW tick. The reconnect line
// counts in seconds, and a counter that advances every 30 s while displaying
// "0:42" is wrong most of the time it is on screen. This is a `setInterval` on
// a number, not a Firestore read — it costs nothing.

import { useEffect, useRef, useState } from "react";
import { listenToFacilityMeta } from "../firebase/firestore";
import { connectionState } from "../domain/controlPanel";

const HEARTBEAT_MS = 5_000;

export function useLiveConnection(facilityId) {
  const [online, setOnline] = useState(false);
  const [lastContactAt, setLastContactAt] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const lastContactRef = useRef(null);

  useEffect(() => {
    if (!facilityId) {
      setOnline(false);
      return undefined;
    }
    let dead = false;
    const unsub = listenToFacilityMeta(
      facilityId,
      (snap) => {
        if (dead) return;
        // `fromCache === false` is the only positive proof of server contact.
        // A cached delivery says nothing about the network, so it must not
        // refresh the timestamp — that would make an offline board look live.
        const fromCache = snap?.metadata?.fromCache !== false;
        setOnline(!fromCache);
        if (!fromCache) {
          const now = new Date();
          lastContactRef.current = now;
          setLastContactAt(now);
        }
      },
      () => {
        // A listener error is not contact. Fail toward "not live" and let the
        // age keep climbing; the band will raise itself on the clock alone.
        if (!dead) setOnline(false);
      }
    );
    return () => {
      dead = true;
      if (typeof unsub === "function") unsub();
    };
  }, [facilityId]);

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), HEARTBEAT_MS);
    return () => clearInterval(id);
  }, []);

  const staleMs = lastContactAt ? nowMs - lastContactAt.getTime() : null;
  const state = connectionState(online, staleMs, lastContactAt, nowMs);

  return { ...state, online, lastContactAt, staleMs };
}

export default useLiveConnection;
